/* ===========================
   src/HomePage.js  (PART 1/2)
   =========================== */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import "./home.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "animate.css";
import WOW from "wowjs";

import { auth, db, rtdb } from "./firebase";
import { signOut } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, onSnapshot, doc } from "firebase/firestore";

// ✅ RTDB (LIVE/OFFLINE only)
import { ref as rtdbRef, onValue, off as rtdbOff } from "firebase/database";

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const DEFAULT_COLLEGE_LEAVE_TIME = "4:10 PM";

  const [transportSettings, setTransportSettings] = useState({
    collegeLeaveTime: DEFAULT_COLLEGE_LEAVE_TIME,
  });

  // ✅ localStorage seen changes re-render trigger
  const [seenBump, setSeenBump] = useState(0);

  const [showBuses, setShowBuses] = useState(false);
  const [selectedBusIndex, setSelectedBusIndex] = useState(null);

  // ✅ Firestore buses map (route/stops/times/routeUpdatedAt)
  const [liveMap, setLiveMap] = useState({});

  // ✅ RTDB live map (live/bus1..bus15)
  const [liveRtdbMap, setLiveRtdbMap] = useState({});

  // ✅ prevent double logout click
  const [loggingOut, setLoggingOut] = useState(false);

  // ✅ keep snapshot unsubs so we can stop them before logout
  const busesUnsubRef = useRef(null);
  const settingsUnsubRef = useRef(null);
  const liveRtdbUnsubRef = useRef(null);

  // ✅ smoother state batching (prevents many re-renders)
  const liveMapRafRef = useRef(null);
  const liveMapPendingRef = useRef(null);

  // ✅ throttle Firestore → UI updates (fast)
  const liveMapThrottleTimerRef = useRef(null);
  const liveMapLastApplyRef = useRef(0);

  // ✅ avoid WOW double init in dev StrictMode
  const wowRef = useRef(null);

  const stats = useMemo(
    () => [
      { label: "Academic Programs", value: 6, suffix: "" },
      { label: "Faculty Members", value: 70, suffix: "+" },
      { label: "Students", value: 1000, suffix: "+" },
    ],
    []
  );

  // ✅ 1 → target
  const [animatedStats, setAnimatedStats] = useState(stats.map(() => 1));
  const animationFrame = useRef(null);

  // ✅ When coming backTo="/?buses=1"
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("buses") === "1") {
      setShowBuses(true);
      setSelectedBusIndex(null);
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.search]);

  // ✅ Stats animation (only for landing page)
  useEffect(() => {
    if (showBuses) return;

    let startTime;
    const duration = 1400;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      setAnimatedStats(
        stats.map((s) => {
          const v = Math.floor(progress * s.value);
          return Math.max(1, v);
        })
      );

      if (progress < 1) {
        animationFrame.current = requestAnimationFrame(animate);
      } else {
        setAnimatedStats(stats.map((s) => s.value));
      }
    };

    animationFrame.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame.current);
  }, [showBuses, stats]);

  // ✅ WOW init (FIXED: dev StrictMode safe + mobile enabled)
  useEffect(() => {
    // allow re-init safely in dev StrictMode
    const isMobile = window.matchMedia?.("(max-width: 768px)")?.matches;

    const wow = new WOW.WOW({
      boxClass: "wow",
      animateClass: "animate__animated",
      offset: 0,
      mobile: true, // ✅ enable animations on mobile too
      live: false,
    });

    try {
      wow.init();
    } catch (e) {}

    wowRef.current = wow;

    const sync = () => {
      try {
        wow.sync();
      } catch (e) {}
    };

    const t = setTimeout(sync, 250);
    window.addEventListener("focus", sync);
    window.addEventListener("resize", sync);

    return () => {
      clearTimeout(t);
      window.removeEventListener("focus", sync);
      window.removeEventListener("resize", sync);
      wowRef.current = null; // ✅ important for dev StrictMode
    };
  }, []);

  // ✅ Smooth reveal-on-scroll (landing page) — FIXED (images/sections will show)
  useEffect(() => {
    if (showBuses) return;

    const run = () => {
      const els = Array.from(document.querySelectorAll(".js-reveal"));
      if (!els.length) return;

      // ✅ lower threshold so sections reveal reliably
      const ENTER_AT = 0.05;

      // fallback if IntersectionObserver not available OR if something breaks
      if (!("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      let observer;
      try {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              // ✅ if ratio is tiny but intersecting, still reveal
              if (entry.intersectionRatio < ENTER_AT && entry.intersectionRatio !== 0)
                return;

              const el = entry.target;
              el.classList.add("is-revealed");
              observer.unobserve(el);
            });
          },
          {
            threshold: [0, ENTER_AT, 0.35, 0.6],
            rootMargin: "0px 0px -5% 0px",
          }
        );
      } catch (e) {
        // if observer creation fails, reveal all
        els.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      const isInViewNow = (el) => {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || 800;
        return r.bottom > 0 && r.top < vh * 0.95;
      };

      els.forEach((el, idx) => {
        const total = els.length;
        const isLast = idx === total - 1;

        const anim = el.dataset.anim || "";
        const delay = el.dataset.delay || "0s";
        el.style.transitionDelay = delay;

        const autoVariant = () => {
          if (isLast) return "reveal--up";
          return idx % 2 === 0 ? "reveal--left" : "reveal--right";
        };

        const pickVariant = (a = "") => {
          const s = String(a).toLowerCase();
          if (s.includes("left")) return "reveal--left";
          if (s.includes("right")) return "reveal--right";
          if (s.includes("down")) return "reveal--down";
          if (s.includes("zoom")) return "reveal--zoom";
          if (s.includes("up")) return "reveal--up";
          return autoVariant();
        };

        el.classList.remove(
          "reveal--left",
          "reveal--right",
          "reveal--down",
          "reveal--zoom",
          "reveal--up",
          "is-revealed"
        );
        el.classList.add(pickVariant(anim));

        // ✅ if already in viewport at load, reveal immediately
        if (isInViewNow(el)) {
          el.classList.add("is-revealed");
          return;
        }

        observer.observe(el);
      });

      return () => {
        try {
          observer.disconnect();
        } catch (e) {}
      };
    };

    // ✅ run after paint (helps dev mode)
    let cleanup;
    const raf = requestAnimationFrame(() => {
      cleanup = run();
    });

    return () => {
      cancelAnimationFrame(raf);
      if (typeof cleanup === "function") cleanup();
    };
  }, [showBuses]);

  // ✅ Firestore listener (buses) — ONLY when buses page open (unchanged)
  useEffect(() => {
    if (busesUnsubRef.current) {
      try {
        busesUnsubRef.current();
      } catch (e) {}
      busesUnsubRef.current = null;
    }

    if (liveMapRafRef.current) {
      cancelAnimationFrame(liveMapRafRef.current);
      liveMapRafRef.current = null;
    }
    liveMapPendingRef.current = null;

    if (liveMapThrottleTimerRef.current) {
      clearTimeout(liveMapThrottleTimerRef.current);
      liveMapThrottleTimerRef.current = null;
    }

    if (!showBuses) {
      setLiveMap({});
      return;
    }

    const scheduleApply = () => {
      if (liveMapRafRef.current) return;

      liveMapRafRef.current = requestAnimationFrame(() => {
        liveMapRafRef.current = null;
        liveMapLastApplyRef.current = Date.now();

        const next = liveMapPendingRef.current || {};
        startTransition(() => setLiveMap(next));
      });
    };

    const unsub = onSnapshot(
      collection(db, "buses"),
      (snap) => {
        const m = {};
        snap.forEach((d) => (m[d.id] = d.data()));
        liveMapPendingRef.current = m;

        const isMobile = window.matchMedia?.("(max-width: 768px)")?.matches;
        const MIN_GAP_MS = isMobile ? 250 : 100;

        const now = Date.now();
        const elapsed = now - (liveMapLastApplyRef.current || 0);

        if (elapsed >= MIN_GAP_MS) {
          if (liveMapThrottleTimerRef.current) {
            clearTimeout(liveMapThrottleTimerRef.current);
            liveMapThrottleTimerRef.current = null;
          }
          scheduleApply();
        } else {
          if (liveMapThrottleTimerRef.current) return;
          liveMapThrottleTimerRef.current = setTimeout(() => {
            liveMapThrottleTimerRef.current = null;
            scheduleApply();
          }, MIN_GAP_MS - elapsed);
        }
      },
      (err) => {
        console.warn("Firestore buses listener error:", err);
        setLiveMap({});
      }
    );

    busesUnsubRef.current = unsub;

    return () => {
      if (busesUnsubRef.current) {
        try {
          busesUnsubRef.current();
        } catch (e) {}
        busesUnsubRef.current = null;
      }
      if (liveMapRafRef.current) {
        cancelAnimationFrame(liveMapRafRef.current);
        liveMapRafRef.current = null;
      }
      liveMapPendingRef.current = null;

      if (liveMapThrottleTimerRef.current) {
        clearTimeout(liveMapThrottleTimerRef.current);
        liveMapThrottleTimerRef.current = null;
      }
    };
  }, [showBuses]);

  // ✅ RTDB listener for LIVE/OFFLINE (unchanged behavior)
  useEffect(() => {
    if (liveRtdbUnsubRef.current) {
      try {
        liveRtdbUnsubRef.current();
      } catch (e) {}
      liveRtdbUnsubRef.current = null;
    }

    if (!showBuses) {
      setLiveRtdbMap({});
      return;
    }

    const r = rtdbRef(rtdb, "live");
    const unsub = onValue(
      r,
      (snap) => setLiveRtdbMap(snap.val() || {}),
      (err) => {
        console.warn("RTDB live listener error:", err);
        setLiveRtdbMap({});
      }
    );

    liveRtdbUnsubRef.current = unsub;

    return () => {
      try {
        unsub();
      } catch (e) {
        try {
          rtdbOff(r);
        } catch (_) {}
      }
      liveRtdbUnsubRef.current = null;
    };
  }, [showBuses]);

  // ✅ settings/transport listener (leave time)
  useEffect(() => {
    if (settingsUnsubRef.current) {
      try {
        settingsUnsubRef.current();
      } catch (e) {}
      settingsUnsubRef.current = null;
    }

    const unsub = onSnapshot(
      doc(db, "settings", "transport"),
      (snap) => {
        if (snap.exists()) {
          const d = snap.data() || {};
          setTransportSettings({
            collegeLeaveTime: d.collegeLeaveTime || DEFAULT_COLLEGE_LEAVE_TIME,
          });
        } else {
          setTransportSettings({ collegeLeaveTime: DEFAULT_COLLEGE_LEAVE_TIME });
        }
      },
      (err) => {
        console.warn("Firestore transport settings listener error:", err);
        setTransportSettings({ collegeLeaveTime: DEFAULT_COLLEGE_LEAVE_TIME });
      }
    );

    settingsUnsubRef.current = unsub;

    return () => {
      if (settingsUnsubRef.current) {
        try {
          settingsUnsubRef.current();
        } catch (e) {}
        settingsUnsubRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, []);

  // ----------------------------
  // Helpers (Live + Route Update)
  // ----------------------------
  const busIdFromIndex = (index) => "bus" + (index + 1);

  // ✅ robust timestamp -> ms
  const tsToMs = (v) => {
    if (!v) return 0;
    if (typeof v === "number") return v;
    if (typeof v === "string") return Number(v) || 0;
    if (v?.toMillis) return v.toMillis();
    if (v?.seconds) return v.seconds * 1000;
    return 0;
  };

  // ✅ LIVE check (2 minutes) — RTDB live/{busId}
  const isBusLive = (busId) => {
    const d = liveRtdbMap?.[busId];
    if (!d) return false;
    if (d.isLive === false) return false;

    const t = tsToMs(d.updatedAtMs || d.updatedAt);
    if (!t) return false;

    return Date.now() - t < 2 * 60 * 1000;
  };

  const handleTrackLive = (busId) => {
    if (!isBusLive(busId)) {
      alert("This bus is not live now.");
      return;
    }
    navigate(`/track/${busId}`);
  };

  // -------- Route update (NO BLINK) + small "jump" --------
  const getRouteUpdatedMs = (busId) => {
    const d = liveMap[busId];
    return tsToMs(d?.routeUpdatedAt);
  };

  const isRouteUpdateUnseen = (busId) => {
    const upd = getRouteUpdatedMs(busId);
    if (!upd) return false;
    const seen = Number(localStorage.getItem(`seenRouteUpdate_${busId}`) || 0);
    return upd > seen;
  };

  const shouldBumpUpdate = (busId) => {
    if (!isRouteUpdateUnseen(busId)) return false;
    const upd = getRouteUpdatedMs(busId);
    if (!upd) return false;
    return Date.now() - upd < 1200; // 1.2 sec only
  };

  const shouldBlinkStops = () => false;

  const markStopsSeen = (busId) => {
    const upd = getRouteUpdatedMs(busId);
    if (upd) {
      localStorage.setItem(`seenRouteUpdate_${busId}`, String(upd));
      setSeenBump((x) => x + 1);
    }
  };

  useEffect(() => {
    if (selectedBusIndex == null) return;
    const busId = busIdFromIndex(selectedBusIndex);
    markStopsSeen(busId);
    // eslint-disable-next-line
  }, [selectedBusIndex]);

  // ✅ ticker text
  const routeUpdatesText = useMemo(() => {
    const msgs = [];
    for (let i = 1; i <= 15; i++) {
      const bid = `bus${i}`;
      if (isRouteUpdateUnseen(bid)) msgs.push(`${bid.toUpperCase()} route updated`);
    }
    return msgs.slice(0, 3).join("   •   ");
    // eslint-disable-next-line
  }, [liveMap, seenBump]);

  // ✅ stops/times from Firestore doc if available (fallback local)
  const getStops = (bus, busId) => {
    const d = liveMap[busId] || {};
    return d.stops || bus.stops || [];
  };

  const getTimes = (bus, busId) => {
    const d = liveMap[busId] || {};
    return d.times || bus.times || [];
  };

  const handleViewBuses = () => {
    setShowBuses(true);
    setSelectedBusIndex(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCloseBuses = () => {
    setShowBuses(false);
    setSelectedBusIndex(null);
    if (location.search) navigate("/", { replace: true });
  };

  const handleViewDetailsInlineStops = (index) => {
    const busId = busIdFromIndex(index);
    markStopsSeen(busId);
    setSelectedBusIndex(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCloseInlineStops = () => {
    setSelectedBusIndex(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);

    try {
      if (busesUnsubRef.current) {
        busesUnsubRef.current();
        busesUnsubRef.current = null;
      }
      if (settingsUnsubRef.current) {
        settingsUnsubRef.current();
        settingsUnsubRef.current = null;
      }
      if (liveRtdbUnsubRef.current) {
        liveRtdbUnsubRef.current();
        liveRtdbUnsubRef.current = null;
      }
    } catch (e) {}

    try {
      await signOut(auth);
    } catch (e) {
      console.error("signOut failed:", e);
    }

    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) keys.push(k);
      }
      keys.forEach((k) => {
        if (k.startsWith("firebase:")) localStorage.removeItem(k);
      });

      sessionStorage.clear();

      if (window.indexedDB) {
        try {
          window.indexedDB.deleteDatabase("firebaseLocalStorageDb");
        } catch (e) {}
      }
    } catch (e) {}

    navigate("/student-login", { replace: true });
    setLoggingOut(false);
  };

  /* ===== PART 1 END (continue in PART 2) ===== */
  /* ===========================
   src/HomePage.js  (PART 2/2)
   =========================== */

  // ----------------------------
  // BUS DATA (UNCHANGED)
  // ----------------------------
  const buses = [
    {
      name: `Bus 1`,
      vehicle: `TN74A1234`,
      routeNo: 1,
      stops: [
        "METUKADAI","INIGONAGER","KURUMPANAI","KODIMUNAI","COLACHAL","THIKANAMCODE","ELANTHA VILAI","MONDAY MARKET","ERANIEL","KANDAN VILAI","MOTTA VILAI","PEYAN KUZHY","PARASERY","MUTHU NEURO","DMI",
      ],
      stopsTamil: [
        "மேட்டுக்கடை","இனிகோநகர்","குறும்பனை","கொடிமுனை","கோலச்சல்","திக்கனங்கோடு","ஏலந்தவிளை","திங்கள் சந்தை","இரணியல்","கந்தன்விளை","மொட்டவிளை","பேயன்குழி","பரசேரி","முத்து நியூரோ","டி.எம்.ஐ",
      ],
      times: ["7.00 AM","7.10 AM","7.12 AM","7.18 AM","7.20 AM","7.40 AM","7.43 AM","7.48 AM","7.50 AM","7.52 AM","7.53 AM","7.55 AM","7.57 AM","8.05 AM","8.50 AM"],
      timesTamil: ["காலை 7.00 AM","7.10 AM","7.12 AM","7.18 AM","7.20 AM","7.40 AM","7.43 AM","7.48 AM","7.50 AM","7.52 AM","7.53 AM","7.55 AM","7.57 AM","8.05 AM","8.50 AM"],
      mapLink: "https://maps.app.goo.gl/tabrNJWLX8aHGCgg6",
      detailsRoute: `/bus1-details`,
    },

    {
      name: `Bus 2`,
      vehicle: `TN74B2234`,
      routeNo: 2,
      stops: [
        "UDAYAPPANKUDRUPU","ZIONPURAM","KUNJAN VILAI","IRULAPPAPURAM","ABDHULUKHADAR HOSPITAL","VELLADICHIVILAI","PARAKAI","KOVALAM","PUTHUGRAMAM","KANYAKUMARI","CHURCH ROAD","VIVEKANANDAPURAM","OTTAYALVILAI","CHINNAMUTTOM","MATHAVAPURAM CHECKPOST","KALLUVILAI","AROKIYAPURAM","DMI",
      ],
      stopsTamil: [
        "உதயப்பன்குட்ருப்பு","சியோன்புரம்","குஞ்சன் விலை","இருளப்பபுரம்","அப்துல் கதாக் மருத்துவமனை","வெல்லாடிச்சிவிலை","பரகை","கோவளம்","புதுகிராமம்","கன்னியாகுமரி","சர்ச் ரோடு","விவேகானந்தபுரம்","ஒட்டயல்விலை","சின்னமுட்டம்","மாதவபுரம் சோதனைச்சாவடி","கல்லுவிலை","அரோக்கியபுரம்","டிஎம்ஐ",
      ],
      times: [
        "7.00 AM","7.05 AM","7.08 AM","7.12 AM","7.15 AM","7.17 AM","7.20 AM","7.45 AM","7.50 AM","7.55 AM","7.57 AM","8.00 AM","8.02 AM","8.05 AM","8.12 AM","8.15 AM","8.20 AM","8.50 AM",
      ],
      timesTamil: [
        "காலை 7.00 AM","7.05 AM","7.08 AM","7.12 AM","7.15 AM","7.17 AM","7.20 AM","7.45 AM","7.50 AM","7.55 AM","7.57 AM","8.00 AM","8.02 AM","8.05 AM","8.12 AM","8.15 AM","8.20 AM","8.50 AM",
      ],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus2-details`,
    },

    {
      name: `Bus 3`,
      vehicle: `TN74C3234`,
      routeNo: 3,
      stops: [
        "UVARI","PALLIVASAL","KADUTHALA","KOTHANKULAM MAIN ROAD","KURINCHI KULAM","IDINTHAKARAI","VAIRAVI KINARU","KUNDANKULAM","PERUMANAL","PUTHUMANAI","CHETIKULAM","KOOTAPULY","KANNAKULAM AMMAN KOIL","PAZHUVOOR","DMI",
      ],
      stopsTamil: [
        "ஊவரை","பள்ளிவாசல்","கடுதலா","கோதாங்குளம் மெயின் ரோடு","குறிஞ்சி குளம்","இதிந்தகரை","வைரவிக் கிணறு","குண்டன்குளம்","பெருமணல்","புதுமனை","சேட்டிக்குளம்","கூட்டாபுளி","கண்ணக்குளம் அம்மன் கோவில்","பழுவூர்","டிஎம்ஐ",
      ],
      times: ["6.50 AM","7.00 AM","7.02 AM","7.20 AM","7.22 AM","7.25 AM","7.30 AM","7.40 AM","7.50 AM","7.55 AM","8.05 AM","8.15 AM","8.20 AM","8.30 AM","8.50 AM"],
      timesTamil: ["காலை 6.50 AM","7.00 AM","7.02 AM","7.20 AM","7.22 AM","7.25 AM","7.30 AM","7.40 AM","7.50 AM","7.55 AM","8.05 AM","8.15 AM","8.20 AM","8.30 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus3-details`,
    },

    {
      name: `Bus 4`,
      vehicle: `TN74D4234`,
      routeNo: 4,
      stops: [
        "MAANJANKUDIERUPPU","CHOTHAVILAI","ELANTHAVILAI","NAINAR PUDUR","SEMPONKARAI BRIDGE","KESAVAN PUTHANTHURAI","PUDUR","ETHAMOZHI","PERIYAKADU","RAJAKKAMANGALAM THURAI","POTTAL VILAKKU","MARAVANKUDIERUPPU","BEACH ROAD JN","SUCEENDRAM","SUCEENDRAM BRIDGE BUS STOP","MYLADI","MARUNGUR","THOPPUR","RAJAVOUR","MUTHUNAGAR 2","ARALVAIMOZHI AROKIA MATHA CHURCH","ARALVAIMOZHI BUS STOP","DMI",
      ],
      stopsTamil: [
        "மாஞ்சங்குடியெருப்பு","சோதவிலை","ஏலந்தவிலை","நைனார்புதூர்","செம்பொன்கரை பாலம்","கேசவன் புதந்துறை","புதூர்","எத்தமொழி","பெரியகாடு","ராஜக்கமங்கலம் துறை","பொட்டல் விளக்கு","மரவங்குடியெருப்பு","பீச் ரோடு சந்திப்பு","சுசீந்திரம்","சுசீந்திரம் பாலம் பேருந்து நிலையம்","மைலாடி","மருங்கூர்","தொப்பூர்","ராஜவூர்","முத்துநகர் 2","அறல்வைமொழி அரோக்கிய மாதா தேவாலயம்","அறல்வைமொழி பேருந்து நிலையம்","டிஎம்ஐ",
      ],
      times: ["7.15 AM","7.17 AM","7.19 AM","7.20 AM","7.23 AM","7.25 AM","7.30 AM","7.33 AM","7.35 AM","7.37 AM","7.40 AM","7.43 AM","7.45 AM","7.55 AM","7.57 AM","8.00 AM","8.04 AM","8.07 AM","8.15 AM","8.25 AM","8.30 AM","8.32 AM","8.50 AM"],
      timesTamil: ["காலை 7.15 AM","7.17 AM","7.19 AM","7.20 AM","7.23 AM","7.25 AM","7.30 AM","7.33 AM","7.35 AM","7.37 AM","7.40 AM","7.43 AM","7.45 AM","7.55 AM","7.57 AM","8.00 AM","8.04 AM","8.07 AM","8.15 AM","8.25 AM","8.30 AM","8.32 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus4-details`,
    },

    {
      name: `Bus 5`,
      vehicle: `TN74E5234`,
      routeNo: 5,
      stops: [
        "MANJANKULAM","PUTHUR","J J NAGAR","KALAKADU BUS STAND","KALAKADU ANNA SALAI","THEPPAKULAM","NORTH SALAI PUDUR","NADU SALAI PUDUR","DONAVOOR","ERVADI","THALAPATHISAMUTHIRAM","OTHAKADAI","ANNA NAGAR","LUTHER NAGER","VALLIYUR BUS STAND","VALLIYUR OLD BUS STAND","VALLIYUR DEPO","SOUTH VALLIYUR","KALANTHAPANAI","PAMPANKULAM","PANAGUDI BUS STAND","PANAGUDI PULIYAMARAM STOP","PANAGUDI CHRUCH","PUNNIYAVALAPURAM","KAVALKINARU JN","DMI",
      ],
      stopsTamil: [
        "மஞ்சங்குளம்","புதூர்","ஜே.ஜே. நகர்","கலக்காடு பேருந்து நிலையம்","கலக்காடு அண்ணா சாலை","தெப்பக்குளம்","வட சாலை புதூர்","நடு சாலை புதூர்","டோனாவூர்","ஏர்வாடி","தளபதி சமுத்திரம்","ஒத்தகடை","அண்ணா நகர்","லூதர் நகர்","வள்ளியூர் பேருந்து நிலையம்","வள்ளியூர் பழைய பேருந்து நிலையம்","வள்ளியூர் டிப்போ","தெற்கு வள்ளியூர்","கலந்தபனை","பாம்பங்குளம்","பாணகுடி பேருந்து நிலையம்","பாணகுடி புளியமரம் நிறுத்தம்","பாணகுடி தேவாலயம்","புன்னியவளாபுரம்","காவல்கிணறு சந்திப்பு","டிஎம்ஐ",
      ],
      times: ["6.55 AM","7.05 AM","7.07 AM","7.12 AM","7.15 AM","7.17 AM","7.22 AM","7.25 AM","7.35 AM","7.45 AM","7.50 AM","7.55 AM","8.00 AM","8.01 AM","8.05 AM","8.07 AM","8.10 AM","8.15 AM","8.18 AM","8.20 AM","8.26 AM","8.28 AM","8.30 AM","8.35 AM","8.40 AM","8.50 AM"],
      timesTamil: ["காலை 6.55 AM","7.05 AM","7.07 AM","7.12 AM","7.15 AM","7.17 AM","7.22 AM","7.25 AM","7.35 AM","7.45 AM","7.50 AM","7.55 AM","8.00 AM","8.01 AM","8.05 AM","8.07 AM","8.10 AM","8.15 AM","8.18 AM","8.20 AM","8.26 AM","8.28 AM","8.30 AM","8.35 AM","8.40 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus5-details`,
    },

    {
      name: `Bus 6`,
      vehicle: `TN74F6234`,
      routeNo: 6,
      stops: [
        "JOSEPH SCHOOL","KADIYAPATTINAM","MUTTOM","AMMANDIVILAI","FRIDAYMARKET","SETHUVOUR","KURUNTHANCODE","SENAPALLI","SARAL","ASARIPALLAM","ANANTHANPALAM","RANITHOTTAM","CHRISTHUNAGAR","VETURNIMADAM","KRISHNANCOIL","THOVALAI","DMI",
      ],
      stopsTamil: [
        "ஜோசப் பள்ளி","கடியாபட்டினம்","முட்டம்","அம்மண்டிவிலை","வெள்ளிக்கிழமை சந்தை","சேதுவூர்","குருந்தன்கோடு","சேனப்பள்ளி","சரல்","ஆசாரிப்பள்ளம்","ஆனந்தன்பாளம்","ராணித்தோட்டம்","கிறிஸ்துநகர்","வெற்றுணிமடம்","கிருஷ்ணன்கோவில்","தோவாலை","டிஎம்ஐ",
      ],
      times: ["7.10 AM","7.15 AM","7.20 AM","7.23 AM","7.26 AM","7.30 AM","7.33 AM","7.35 AM","7.37 AM","7.50 AM","7.55 AM","7.57 AM","8.00 AM","8.10 AM","8.13 AM","8.30 AM","8.50 AM"],
      timesTamil: ["காலை 7.10 AM","7.15 AM","7.20 AM","7.23 AM","7.26 AM","7.30 AM","7.33 AM","7.35 AM","7.37 AM","7.50 AM","7.55 AM","7.57 AM","8.00 AM","8.10 AM","8.13 AM","8.30 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus6-details`,
    },

    {
      name: `Bus 7`,
      vehicle: `TN74G7234`,
      routeNo: 7,
      stops: [
        "THADIKARAN KONAM","KESAVAN PUDUR","ETTAMADAI","AZHLAGIAPANDIPURAM","DERISANAMCOPE","THITUVILAI","THUVARANKADU","SETHAPAL","SENBAGARAMAN PUDUR","MATHAVALAYAM","LAYAM","CHRISTHU NAGAR","KUMARAN PUDUR","THOVALAI BRIDGE","MUTHUNAGAR 1","MOUNT CHURCH","DMI",
      ],
      stopsTamil: [
        "தடிகாரன் கோணம்","கேசவன் புதூர்","எட்டமடை","அழகியபாண்டிபுரம்","தேரிசனம்கோப்","தித்துவிலை","துவாரங்காடு","சேதபால்","செம்பகராமன் புதூர்","மாதவலயம்","லயம்","கிறிஸ்து நகர்","குமரன் புதூர்","தோவாலை பாலம்","முத்துநகர் 1","மவுண்ட் தேவாலயம்","டிஎம்ஐ",
      ],
      times: ["7.40 AM","7.45 AM","7.47 AM","7.50 AM","7.55 AM","7.58 AM","8.00 AM","8.07 AM","8.15 AM","8.18 AM","8.20 AM","8.22 AM","8.24 AM","8.27 AM","8.30 AM","8.35 AM","8.50 AM"],
      timesTamil: ["காலை 7.40 AM","7.45 AM","7.47 AM","7.50 AM","7.55 AM","7.58 AM","8.00 AM","8.07 AM","8.15 AM","8.18 AM","8.20 AM","8.22 AM","8.24 AM","8.27 AM","8.30 AM","8.35 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus7-details`,
    },

    { name: `Bus 8`, vehicle: `TN74H8234`, routeNo: 8, stops: ["Currently Not Available"], stopsTamil: [""], times: [""], timesTamil: [""], mapLink: "https://maps.google.com", detailsRoute: `/bus8-details` },
    { name: `Bus 9`, vehicle: `TN74I9234`, routeNo: 9, stops: ["Currently Not Available"], stopsTamil: [""], times: [""], timesTamil: [""], mapLink: "https://maps.google.com", detailsRoute: `/bus9-details` },

    {
      name: `Bus 10`,
      vehicle: `TN74J0234`,
      routeNo: 10,
      stops: ["PUTHUKADAI","KAPIKADU","CHENNITHOTTAM","MARTHANDAM","KALLUPALAM","ATHUR","POOVAN CODE","VERKILAMPI","MEKKAMANDAPAM","AZHGIYA MANDAPAM","THAKALAI","VILLUKURI","DERIK MAHINDRA THOTIYODU","KALIYANKADU BUS STOP","PARVATHIPURAM","KATTAYAN VILAI","GOLDEN BAKERY","THIRAVIYAM HOSPITAL","THERAIKAL PUDUR 1","THERAIKAL PUDUR 2"],
      stopsTamil: ["புதுக்கடை","காப்பிக்காடு","சென்னித்தோட்டம்","மார்த்தாண்டம்","கல்லுப்பாலம்","ஆத்தூர்","பூவன்கோடு","வெர்கிலாம்பி","மேக்கமண்டபம்","அழகிய மண்டபம்","தாக்கலை","வில்லுக்குறி","டெரிக் மஹிந்திரா தோட்டியோடு","கலியாணக்காடு பேருந்து நிறுத்தம்","பார்வதிபுரம்","கட்டாயன்விலை","கோல்டன் பேக்கரி","திராவியம் மருத்துவமனை","தேரைக்கால் புதூர் 1","தேரைக்கால் புதூர் 2"],
      times: ["6.55 AM","7.05 AM","7.10 AM","7.12 AM","7.17 AM","7.25 AM","7.35 AM","7.40 AM","7.45 AM","7.50 AM","7.53 AM","8.00 AM","8.05 AM","8.10 AM","8.12 AM","8.14 AM","8.16 AM","8.20 AM","8.22 AM","8.24 AM"],
      timesTamil: ["காலை 6.55 AM","7.05 AM","7.10 AM","7.12 AM","7.17 AM","7.25 AM","7.35 AM","7.40 AM","7.45 AM","7.50 AM","7.53 AM","8.00 AM","8.05 AM","8.10 AM","8.12 AM","8.14 AM","8.16 AM","8.20 AM","8.22 AM","8.24 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus10-details`,
    },

    {
      name: `Bus 11`,
      vehicle: `TN74K1234`,
      routeNo: 11,
      stops: ["SANGANERI","KOTAVILAI","NAKKANERI","IRUKANTHURAI","URALVAIMOZHI","KEELKULAM","MAARANKULAM","SANGANAPURAM","JACOPURAM","CHIDAMPARAPURAM JN","PILLAIYARKUDIRUPU BUS STOP","MATHAGANERI","SEMPIKULAM","ALAGANERY","CONCORDIA SCHOOL","MELA KILAKULAM","AVARAIKULAM IOB","SOUNDARALINGAPURAM","PUTHIYAMPUTHUR","MADANADAR KUDIRUPU","KUMARAPURAM"],
      stopsTamil: ["சங்கனேரி","கோடவிலை","நக்கனேரி","இருகந்துறை","உறல்வைமொழி","கீழ்குளம்","மாறன்குளம்","சங்கனபுரம்","ஜேக்கோபுரம்","சிதம்பரபுரம் சந்திப்பு","பிள்ளையர்குடிருப்பு பேருந்து நிறுத்தம்","மாதகனேரி","செம்பிகுளம்","அழகனேரி","காங்கோர்டியா பள்ளி","மேல கிழக்குளம்","ஆவரைக்குளம் ஐஓபி","சௌந்தரலிங்கபுரம்","புதியம்புதூர்","மடநாடர் குடிருப்பு","குமரபுரம்"],
      times: ["7.00 AM","7.05 AM","7.07 AM","7.10 AM","7.20 AM","7.25 AM","7.27 AM","7.38 AM","7.48 AM","7.50 AM","7.55 AM","8.00 AM","8.02 AM","8.07 AM","8.09 AM","8.12 AM","8.17 AM","8.20 AM","8.22 AM","8.25 AM","8.28 AM","8.45 AM"],
      timesTamil: ["காலை 7.00 AM","7.05 AM","7.07 AM","7.10 AM","7.20 AM","7.25 AM","7.27 AM","7.38 AM","7.48 AM","7.50 AM","7.55 AM","8.00 AM","8.02 AM","8.07 AM","8.09 AM","8.12 AM","8.17 AM","8.20 AM","8.22 AM","8.25 AM","8.28 AM","8.45 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus11-details`,
    },

    {
      name: `Bus 12`,
      vehicle: `TN74L2234`,
      routeNo: 12,
      stops: ["THURAIKUDIERUPPU","KALLIKULAM","SOUNDARAPANDIPURAM","SAMATHUVAPURAM","PARAMESWARAPURAM","UDAYATHOOR","RADHAPURAM","THANAKKARKULAM","KOZHIYANKULAM","PALLAVILAI","MANICKAMPUDUR","S S PURAM","ANTONY NAGAR","TMB ATM STOP","VADAKKANKULAM","VADAKKANKULAM EB","SOUTH PERUNGUDI","NORTH PERUNGUDI","VEPILANKULAM","DMI"],
      stopsTamil: ["துறைகுடியெருப்பு","கள்ளிக்குளம்","சௌந்தரபாண்டிபுரம்","சமத்துவபுரம்","பரமேஸ்வரபுரம்","உதயத்தூர்","ராதாபுரம்","தனக்கர்குளம்","கோழியங்குளம்","பள்ளவிலை","மாணிக்கம்புதூர்","எஸ்.எஸ்.புரம்","ஆண்டனி நகர்","டிஎம்பி ஏடிஎம் நிறுத்தம்","வடக்கங்குளம்","வடக்கங்குளம் ஈபி","தெற்கு பெருங்குடி","வடக்கு பெருங்குடி","வேப்பிலாங்குளம்","டிஎம்ஐ"],
      times: ["6.50 AM","6.55 AM","7.00 AM","7.05 AM","7.15 AM","7.20 AM","7.25 AM","7.37 AM","7.42 AM","7.45 AM","7.52 AM","7.55 AM","8.00 AM","8.05 AM","8.07 AM","8.08 AM","8.12 AM","8.14 AM","8.20 AM","8.50 AM"],
      timesTamil: ["காலை 6.50 AM","6.55 AM","7.00 AM","7.05 AM","7.15 AM","7.20 AM","7.25 AM","7.37 AM","7.42 AM","7.45 AM","7.52 AM","7.55 AM","8.00 AM","8.05 AM","8.07 AM","8.08 AM","8.12 AM","8.14 AM","8.20 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus12-details`,
    },

    {
      name: `Bus 13`,
      vehicle: `TN74M3234`,
      routeNo: 13,
      stops: ["THIRUKANKUDI","ROSEMIYAPURAM","DARMALINGAPURAM","THALAVAIPURAM WEST","THALAVAIPURAM EAST","PANAGUDI IOB","LEBBAI KUDIERUPPU MUTHU MANDAPAM","LEBBAI KUDIERUPPU","PERIYANAYAGIPURAM","PERIYANAYAGIPURAM SCHOOL","KAVALKINARU PUDUR","KAVALKINARU DEVAKOTTAI","KAVALKINARU BUS STAND","KAVALKINARU SOUTH","DMI"],
      stopsTamil: ["திருக்கங்குடி","ரோஸ்மியபுரம்","தர்மலிங்கபுரம்","தலவையைபுரம் மேற்கு","தலவையைபுரம் கிழக்கு","பாணகுடி ஐஓபி","லெப்பை குடியெருப்பு முத்து மண்டபம்","லெப்பை குடியெருப்பு","பெரியநாயகிபுரம்","பெரியநாயகிபுரம் பள்ளி","காவல்கிணறு புதூர்","காவல்கிணறு தேவகோட்டை","காவல்கிணறு பேருந்து நிலையம்","காவல்கிணறு தெற்கு","டிஎம்ஐ"],
      stopsTamil: ["திருக்கங்குடி","ரோஸ்மியபுரம்","தர்மலிங்கபுரம்","தலவையைபுரம் மேற்கு","தலவையைபுரம் கிழக்கு","பாணகுடி ஐஓபி","லெப்பை குடியெருப்பு முத்து மண்டபம்","லெப்பை குடியெருப்பு","பெரியநாயகிபுரம்","பெரியநாயகிபுரம் பள்ளி","காவல்கிணறு புதூர்","காவல்கிணறு தேவகோட்டை","காவல்கிணறு பேருந்து நிலையம்","காவல்கிணறு தெற்கு","டிஎம்ஐ"],
      times: ["7.35 AM","7.40 AM","7.45 AM","7.50 AM","7.55 AM","8.00 AM","8.15 AM","8.18 AM","8.20 AM","8.23 AM","8.27 AM","8.30 AM","8.32 AM","8.35 AM","8.50 AM"],
      timesTamil: ["காலை 7.35 AM","7.40 AM","7.45 AM","7.50 AM","7.55 AM","8.00 AM","8.15 AM","8.18 AM","8.20 AM","8.23 AM","8.27 AM","8.30 AM","8.32 AM","8.35 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus13-details`,
    },

    {
      name: `Bus 14`,
      vehicle: `TN74N4234`,
      routeNo: 14,
      stops: ["MELASURANKUDI","KURUSADI 1","KURUSADI 2","HOUSING BOARD","PUNNAI NAGAR","M L HOSPITAL","CARMEL","RAMANPUDUR","CHETIKULAM","PANAVILAI","ANNA BUS STAND","KOTTAR POLICE STATION","VADIVEESWARAM","MEENASHIPURAM","KOTTAR FIRE STATION","VADASERY FOREST OFFICE","SMRV SCHOOL","PUTHERY","ERACHAKULAM","THALAKUDI"],
      stopsTamil: ["மேலசுரங்குடி","குருசடி 1","குருசடி 2","ஹவுஸிங் போர்டு","புன்னை நகர்","எம்.எல். மருத்துவமனை","கார்மல்","ராமன்புதூர்","சேட்டிக்குளம்","பாணவிலை","அண்ணா பேருந்து நிலையம்","கோட்டார் போலீஸ் ஸ்டேஷன்","வடிவீஸ்வரம்","மீனாசிபுரம்","கோட்டார் தீயணைப்பு நிலையம்","வடசேரி காடு அலுவலகம்","எஸ்எம்ஆர்வி பள்ளி","புதேரி","ஏராசாகுளம்","தளக்குடி"],
      times: ["7.50 AM","7.52 AM","7.54 AM","7.56 AM","7.58 AM","8.00 AM","8.02 AM","8.04 AM","8.07 AM","8.09 AM","8.12 AM","8.14 AM","8.16 AM","8.18 AM","8.20 AM","8.23 AM","8.25 AM","8.27 AM","8.32 AM","8.35 AM"],
      timesTamil: ["காலை 7.50 AM","7.52 AM","7.54 AM","7.56 AM","7.58 AM","8.00 AM","8.02 AM","8.04 AM","8.07 AM","8.09 AM","8.12 AM","8.14 AM","8.16 AM","8.18 AM","8.20 AM","8.23 AM","8.25 AM","8.27 AM","8.32 AM","8.35 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus14-details`,
    },

    {
      name: `Bus 15`,
      vehicle: `TN74O5234`,
      routeNo: 15,
      stops: ["MELAMANAKUDI","SOUTH THAMARAKULAM","MUKILENKUDIERUPPU","AGESTEESWARAM PILLAYER COIL","KOTTARAM","MAHARAJAPURAM","ACHANKULAM","OSARAVILAI","MYLADIPUDUR","PUNNARKULAM","AZHAGAPPAPURAM","JAMESTOWN","ANJUGRAMAM","LEVENJIPURAM","SIVAGANAPURAM","AMBALAVANAPURAM","DMI"],
      stopsTamil: ["மேலமானகுடி","தெற்கு தாமரைக்குளம்","முகிலெங்குடியெருப்பு","அஜெஸ்தீஸ்வரம் பிள்ளையர் கோவில்","கோட்டாரம்","மகாராஜபுரம்","அச்சங்குளம்","ஓசரவிலை","மைலாடிப்புதூர்","புண்ணர்குளம்","அழகப்பபுரம்","ஜேம்ஸ்டவுன்","அஞ்சுராமம்","லெவன் ஜிபுரம்","சிவகனபுரம்","அம்பாளவனபுரம்","டிஎம்ஐ"],
      times: ["7.10 AM","7.20 AM","7.25 AM","7.37 AM","7.47 AM","7.50 AM","7.55 AM","7.48 AM","8.00 AM","8.04 AM","8.07 AM","8.10 AM","8.15 AM","8.17 AM","8.19 AM","8.30 AM","8.32 AM","8.50 AM"],
      timesTamil: ["காலை 7.10 AM","7.20 AM","7.25 AM","7.37 AM","7.47 AM","7.50 AM","7.55 AM","7.48 AM","8.00 AM","8.04 AM","8.07 AM","8.10 AM","8.15 AM","8.17 AM","8.19 AM","8.30 AM","8.32 AM","8.50 AM"],
      mapLink: "https://maps.google.com",
      detailsRoute: `/bus15-details`,
    },
  ];

  const renderStatusPill = (live) => (
    <span
      className="badge"
      style={{
        background: live ? "rgba(21,199,119,.16)" : "rgba(255,77,109,.16)",
        border: `1px solid ${
          live ? "rgba(21,199,119,.35)" : "rgba(255,77,109,.35)"
        }`,
        color: live ? "rgba(234,255,246,.95)" : "rgba(255,230,235,.95)",
        fontWeight: 900,
        padding: "6px 10px",
        borderRadius: 12,
        letterSpacing: 0.6,
      }}
    >
      {live ? "LIVE" : "OFFLINE"}
    </span>
  );

  const renderUpdatedBadge = (busId) => {
    if (!isRouteUpdateUnseen(busId)) return null;
    return (
      <span
        className="badge"
        style={{
          background: "rgba(255,193,7,.14)",
          border: "1px solid rgba(255,193,7,.34)",
          color: "rgba(255,255,255,.92)",
          fontWeight: 900,
          padding: "5px 9px",
          borderRadius: 12,
          letterSpacing: 0.5,
        }}
        title="Route updated. Please check stops."
      >
        UPDATED
      </span>
    );
  };

  return (
    <div className="hp" style={{ minHeight: "100vh" }}>
      <style>{`
        html { scroll-behavior: smooth; }

        .glass-pro{
          position: relative;
          border-radius: 22px;
          background:
            linear-gradient(180deg, rgba(255,255,255,.13), rgba(255,255,255,.06));
          border: 1px solid rgba(255,255,255,.18);
          box-shadow: 0 22px 70px rgba(0,0,0,.42);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          overflow: hidden;
        }
        .glass-pro::before{
          content:"";
          position:absolute;
          inset:-2px;
          background:
            radial-gradient(900px 420px at 15% 10%, rgba(47,123,255,.22), transparent 55%),
            radial-gradient(900px 420px at 90% 20%, rgba(255,77,109,.18), transparent 55%);
          opacity:.55;
          pointer-events:none;
        }
        .glass-pro > *{ position:relative; z-index:1; }

        .hero-title{ text-shadow: 0 18px 48px rgba(0,0,0,.55); }
        .hero-sub{ text-shadow: 0 12px 34px rgba(0,0,0,.45); }

        .js-reveal{
          will-change: transform, opacity;
          backface-visibility: hidden;
          opacity: 0;
          transform: translate3d(0, 18px, 0);
          transition:
            opacity .65s cubic-bezier(.2,.8,.2,1),
            transform .65s cubic-bezier(.2,.8,.2,1);
        }
        .js-reveal.reveal--left{  transform: translate3d(-18px, 0, 0); }
        .js-reveal.reveal--right{ transform: translate3d( 18px, 0, 0); }
        .js-reveal.reveal--down{  transform: translate3d(0, -18px, 0); }
        .js-reveal.reveal--zoom{  transform: scale(.96); }
        .js-reveal.is-revealed{ opacity: 1; transform: translate3d(0,0,0); }

        .stats-strip{
          display:flex;
          gap: 10px;
          align-items: stretch;
          justify-content: space-between;
          width:100%;
        }
        .stats-chip{
          flex: 1;
          padding: 14px 14px;
          border-radius: 18px;
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.12);
        }
        .stats-num{
          font-weight: 900;
          color: #fff;
          font-size: clamp(1.3rem, 2.05vw, 1.6rem);
          line-height: 1;
          letter-spacing: .2px;
          white-space: nowrap;
        }
        .stats-lbl{
          margin-top: 8px;
          color: rgba(234,240,255,.72);
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
          font-size: 11px;
        }
        .stats-desc{
          margin-top: 8px;
          color: rgba(234,240,255,.62);
          font-size: 13px;
          line-height: 1.5;
        }
        @media (max-width: 768px){
          .stats-strip{ flex-direction: column; }
          .stats-num{ font-size: 1.5rem; }
        }

        .hp-banner{ margin-top: 8px; display: grid; gap: 6px; }
        .hp-leave-line{
          color: rgba(234,240,255,.78);
          font-size: 12.5px;
          font-weight: 800;
          letter-spacing: .2px;
        }
        .hp-leave-line b{ color: #fff; font-weight: 900; }

        /* ✅ ticker */
        .hp-ticker{
          position: relative;
          overflow: hidden;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,.12);
          background: rgba(255,255,255,.06);
          height: 28px;
          display:flex;
          align-items:center;
        }
        .hp-ticker__move{
          display:inline-block;
          white-space: nowrap;
          padding-right: 100%;
          animation: hpTickerMove 11s linear infinite;
          color: rgba(255,255,255,.88);
          font-size: 12px;
          font-weight: 900;
          letter-spacing: .2px;
          transform: translateX(100%);
        }
        @keyframes hpTickerMove{
          from{ transform: translateX(100%); }
          to{ transform: translateX(-100%); }
        }
        @media (prefers-reduced-motion: reduce){
          .hp-ticker__move{ animation: none; transform: none; padding-right: 0; padding-left: 12px; }
        }

        /* ✅ NO BLINK. Only small "bump" once (update வந்ததும்) */
        .bus-card--bump{
          border: 1px solid rgba(255,193,7,.32) !important;
          box-shadow: 0 0 0 1px rgba(255,193,7,.12), 0 14px 44px rgba(255,193,7,.12);
          animation: bumpOnce .28s ease-out 1;
        }
        @keyframes bumpOnce{
          0%{ transform: translateY(0) scale(1); filter: brightness(1); }
          60%{ transform: translateY(-3px) scale(1.01); filter: brightness(1.06); }
          100%{ transform: translateY(0) scale(1); filter: brightness(1); }
        }
        .btn--bump{
          animation: btnPop .22s ease-out 1;
        }
        @keyframes btnPop{
          0%{ transform: scale(1); }
          60%{ transform: scale(1.03); }
          100%{ transform: scale(1); }
        }

        /* ✅ BUS PAGE anti-flicker */
        #buses .glass{
          transform: translate3d(0,0,0);
          -webkit-transform: translate3d(0,0,0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          will-change: transform;
        }

        /* ✅ Mobile performance */
        @media (max-width: 768px){
          .glass-pro{
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            box-shadow: 0 14px 44px rgba(0,0,0,.38);
          }
          #buses .glass{
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            box-shadow: 0 12px 34px rgba(0,0,0,.34);
          }
          .hp-ticker__move{
            animation-duration: 14s;
          }
        }
      `}</style>

      {/* ================== Navbar ================== */}
      <nav className="navbar navbar-expand-lg navbar-dark glass-navbar fixed-top wow animate__animated animate__fadeInDown animate__slower">
        <div className="container">
          <a className="navbar-brand fw-bold" href="#home" style={{ letterSpacing: 0.3 }}>
            DMI ENGINEERING COLLEGE
          </a>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              {!showBuses && selectedBusIndex === null && (
                <>
                  <li className="nav-item">
                    <a className="nav-link active" href="#home">Home</a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#about">About</a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#courses">Courses</a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#achievements">Achievements</a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" href="#contact">Contact</a>
                  </li>
                </>
              )}

              {showBuses && (
                <li className="nav-item ms-lg-2">
                  <button className="btn btn-sm btn-outline-light" onClick={handleCloseBuses}>
                    Back to Home
                  </button>
                </li>
              )}

              <li className="nav-item ms-lg-2">
                <button className="btn btn-sm btn-danger" onClick={handleLogout} disabled={loggingOut}>
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* ================== HERO ================== */}
      {!showBuses && (
        <section id="home" className="position-relative" style={{ paddingTop: 74 }}>
          <img
            src="/dmiii2.jpg"
            alt="DMI College"
            className="hero-img wow animate__animated animate__slideInDown animate__slow"
            style={{
              width: "100%",
              maxHeight: 540,
              objectFit: "cover",
              display: "block",
              borderBottomLeftRadius: 18,
              borderBottomRightRadius: 18,
            }}
          />

          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(6,11,26,.10) 0%, rgba(6,11,26,.32) 45%, rgba(6,11,26,.78) 100%)",
              borderBottomLeftRadius: 18,
              borderBottomRightRadius: 18,
            }}
          />

          <div
            className="wow animate__animated animate__fadeInUp animate__slow"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "18%",
              padding: "0 14px",
            }}
          >
            <div className="mx-auto" style={{ maxWidth: 980, textAlign: "center" }}>
              <div
                style={{
                  display: "inline-block",
                  padding: "6px 10px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,.16)",
                  background: "rgba(0,0,0,.18)",
                  color: "rgba(255,255,255,.88)",
                  fontWeight: 900,
                  fontSize: 12,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Transport & Academic Excellence
              </div>

              <h1 className="fw-bold text-white mb-2 hero-title" style={{ letterSpacing: 0.2 }}>
                Welcome to DMI Engineering College
              </h1>

              <p className="mb-3 hero-sub" style={{ color: "rgba(234,240,255,.78)" }}>
                Smart campus experience with live bus tracking, route updates and modern facilities.
              </p>

              <div className="d-flex justify-content-center gap-2 flex-wrap" style={{ marginTop: 65 }}>
                <button className="btn btn-glass-primary btn-lg" onClick={handleViewBuses}>
                  View Bus Details
                </button>

                <a href="#about" className="btn btn-outline-light btn-lg">
                  Explore College
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================== STATS ================== */}
      {!showBuses && (
        <section className="js-reveal" data-anim="fadeInUp" data-delay="0.02s" style={{ marginTop: -24 }}>
          <div className="container">
            <div className="glass-pro" style={{ padding: 14 }}>
              <div className="stats-strip">
                {stats.map((stat, i) => (
                  <div key={stat.label} className="stats-chip">
                    <div className="stats-num">
                      {animatedStats[i]}
                      {stat.suffix}
                    </div>
                    <div className="stats-lbl">{stat.label}</div>
                    <div className="stats-desc">
                      {stat.label === "Academic Programs" && "Industry-aligned, updated curriculum"}
                      {stat.label === "Faculty Members" && "Experienced mentors across departments"}
                      {stat.label === "Students" && "Active campus community and placements focus"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================== ABOUT ================== */}
      {!showBuses && (
        <section id="about" className="py-5">
          <div className="container">
            <h2 className="fw-bold text-white text-center mb-4 js-reveal" data-anim="fadeInLeft" data-delay="0.02s">
              About Our College
            </h2>

            <div className="row align-items-center g-4">
              <div className="col-md-6 js-reveal" data-anim="fadeInUp" data-delay="0.04s">
                <div className="glass-pro p-4">
                  <p className="mb-0" style={{ color: "rgba(234,240,255,.80)", lineHeight: 1.85 }}>
                    DMI Engineering College, Kanyakumari District (Affiliated to Anna University) provides
                    quality engineering education with strong lab facilities, placement focus and safe transport.
                    Our transport system covers multiple routes to ensure students reach campus comfortably.
                  </p>
                </div>
              </div>

              <div className="col-md-6 js-reveal" data-anim="fadeInRight" data-delay="0.06s">
                <div className="glass-pro p-2">
                  <img
                    src="/image2.jpg"
                    alt="Campus Life"
                    className="img-fluid rounded-4"
                    style={{ width: "100%", maxHeight: 340, objectFit: "cover" }}
                  />
                </div>
              </div>

              <div className="col-md-6 js-reveal" data-anim="fadeInLeft" data-delay="0.08s">
                <div className="glass-pro p-4">
                  <ul className="mb-0" style={{ color: "rgba(234,240,255,.80)", lineHeight: 2 }}>
                    <li>Affiliated to Anna University</li>
                    <li>Modern labs and practical learning</li>
                    <li>Reliable transport network and safety</li>
                    <li>Experienced faculty and student support</li>
                  </ul>
                </div>
              </div>

              <div className="col-md-6 js-reveal" data-anim="fadeInUp" data-delay="0.10s">
                <div className="glass-pro p-2">
                  <img
                    src="/image4.jpg"
                    alt="Facilities"
                    className="img-fluid rounded-4"
                    style={{ width: "100%", maxHeight: 340, objectFit: "cover" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================== COURSES ================== */}
      {!showBuses && (
        <section id="courses" className="py-5">
          <div className="container">
            <h2 className="fw-bold text-white text-center mb-4 js-reveal" data-anim="fadeInDown" data-delay="0.02s">
              Courses Offered
            </h2>

            <div className="row g-3 justify-content-center">
              {[
                { name: "B.E. Computer Science & Engineering", anim: "fadeInUp", delay: "0.04s" },
                { name: "B.E. Mechanical Engineering", anim: "fadeInUp", delay: "0.10s" },
                { name: "B.E. Electrical & Electronics Engineering", anim: "fadeInUp", delay: "0.16s" },
                { name: "B.E. Civil Engineering", anim: "fadeInUp", delay: "0.22s" },
              ].map((c) => (
                <div key={c.name} className="col-12 col-md-3 js-reveal" data-anim={c.anim} data-delay={c.delay}>
                  <div
                    className="glass-pro p-3"
                    style={{ height: "100%", transition: "transform .18s ease" }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                  >
                    <div style={{ fontWeight: 900, color: "#fff", fontSize: 15, lineHeight: 1.4 }}>
                      {c.name}
                    </div>
                    <div style={{ marginTop: 10, color: "rgba(234,240,255,.65)", fontSize: 13 }}>
                      Practical learning, labs and placement readiness.
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================== ACHIEVEMENTS ================== */}
      {!showBuses && (
        <section id="achievements" className="py-5">
          <div className="container text-center">
            <h2 className="fw-bold text-white mb-3 js-reveal" data-anim="fadeInDown" data-delay="0.02s">
              Achievements
            </h2>
            <div className="glass-pro p-4 mx-auto js-reveal" data-anim="fadeInUp" data-delay="0.06s" style={{ maxWidth: 920 }}>
              <p className="mb-0" style={{ color: "rgba(234,240,255,.78)", lineHeight: 1.85 }}>
                Our students consistently excel in academics, innovation and placements.
                The institution is recognized for excellence in engineering education across Tamil Nadu.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ================== BUS ROUTES PAGE ================== */}
      {showBuses && (
        <section id="buses" style={{ paddingTop: 92, paddingBottom: 40 }}>
          <div className="container">
            <div
              className="glass d-flex align-items-center justify-content-between gap-3 mb-4"
              style={{
                padding: "12px 14px",
                position: "sticky",
                top: 78,
                zIndex: 20,
              }}
            >
              <div>
                <div style={{ fontWeight: 900, color: "#fff", fontSize: 18 }}>
                  Bus Routes
                </div>

                <div className="hp-banner">
                  <div className="hp-leave-line">
                    Bus leave college:{" "}
                    <b>{transportSettings.collegeLeaveTime || DEFAULT_COLLEGE_LEAVE_TIME}</b>
                  </div>

                  {routeUpdatesText ? (
                    <div className="hp-ticker" title={routeUpdatesText}>
                      <div className="hp-ticker__move">{routeUpdatesText}</div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-icon" onClick={handleCloseBuses} aria-label="Close bus routes">
                  <span style={{ fontWeight: 900, fontSize: 16 }}>×</span>
                </button>
              </div>
            </div>

            {selectedBusIndex !== null ? (
              (() => {
                const bus = buses[selectedBusIndex];
                const busId = busIdFromIndex(selectedBusIndex);
                const live = isBusLive(busId);

                const stops = getStops(bus, busId);
                const times = getTimes(bus, busId);
                const rows = Math.max(stops.length, times.length);

                return (
                  <div className="mx-auto" style={{ maxWidth: 980 }}>
                    <div className="glass p-4">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h4 className="m-0" style={{ fontWeight: 900, color: "#fff" }}>
                              {bus.name} Stops
                            </h4>
                            {renderStatusPill(live)}
                            {renderUpdatedBadge(busId)}
                          </div>
                          <div style={{ marginTop: 6, color: "rgba(234,240,255,.68)", fontSize: 13 }}>
                            Vehicle: <span style={{ color: "#fff", fontWeight: 800 }}>{bus.vehicle}</span>{" "}
                            · Route No: <span style={{ color: "#fff", fontWeight: 800 }}>{bus.routeNo}</span>
                          </div>
                        </div>

                        <div className="d-flex gap-2 flex-wrap">
                          <button className="btn btn-outline-light" onClick={handleCloseInlineStops}>
                            Back
                          </button>
                        </div>
                      </div>

                      <div
                        className="mt-3"
                        style={{
                          overflow: "hidden",
                          borderRadius: 16,
                          border: "1px solid rgba(255,255,255,.14)",
                        }}
                      >
                        <div style={{ overflowX: "auto" }}>
                          <table className="table table-sm mb-0" style={{ color: "rgba(234,240,255,.82)" }}>
                            <thead style={{ background: "rgba(255,255,255,.08)" }}>
                              <tr>
                                <th style={{ width: 60, color: "#fff" }}>#</th>
                                <th style={{ color: "#fff" }}>Stop</th>
                                <th style={{ width: 160, color: "#fff" }}>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.from({ length: rows }).map((_, i) => (
                                <tr key={i} style={{ borderColor: "rgba(255,255,255,.10)" }}>
                                  <td>{i + 1}</td>
                                  <td>{stops[i] || "-"}</td>
                                  <td>{times[i] || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="d-flex gap-2 flex-wrap mt-3">
                        <button
                          className={`btn ${live ? "btn-track-live" : "btn-track-offline"}`}
                          onClick={() => handleTrackLive(busId)}
                        >
                          Track Live
                        </button>

                        <button className="btn btn-glass-primary" onClick={() => navigate(bus.detailsRoute)}>
                          View Details
                        </button>

                        <button className="btn btn-outline-light" onClick={handleCloseInlineStops}>
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="row g-3">
                {buses.map((bus, index) => {
                  const busId = busIdFromIndex(index);
                  const live = isBusLive(busId);

                  const bump = shouldBumpUpdate(busId);
                  const unseen = isRouteUpdateUnseen(busId);

                  return (
                    <div key={busId} className="col-12 col-md-6 col-lg-4">
                      <div className={`glass p-3 h-100 ${bump ? "bus-card--bump" : ""}`}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <div style={{ fontWeight: 900, color: "#fff", fontSize: 16 }}>{bus.name}</div>
                              {renderStatusPill(live)}
                              {unseen ? renderUpdatedBadge(busId) : null}
                            </div>
                            <div style={{ marginTop: 8, color: "rgba(234,240,255,.68)", fontSize: 13, lineHeight: 1.6 }}>
                              <div>Vehicle: <span style={{ color: "#fff", fontWeight: 800 }}>{bus.vehicle}</span></div>
                              <div>Route No: <span style={{ color: "#fff", fontWeight: 800 }}>{bus.routeNo}</span></div>
                            </div>
                          </div>

                          <button className="btn btn-outline-light btn-sm" onClick={() => navigate(bus.detailsRoute)}>
                            View Details
                          </button>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            className={`btn btn-glass-primary btn-sm flex-fill ${bump ? "btn--bump" : ""}`}
                            onClick={() => handleViewDetailsInlineStops(index)}
                          >
                            View Stops
                          </button>

                          <button
                            className={`btn btn-sm flex-fill ${live ? "btn-track-live" : "btn-track-offline"}`}
                            onClick={() => handleTrackLive(busId)}
                          >
                            Track Live
                          </button>
                        </div>

                        {unseen && (
                          <div style={{ marginTop: 10, color: "rgba(234,240,255,.62)", fontSize: 12 }}>
                            Route updated. Please check stops once.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================== CONTACT ================== */}
      {!showBuses && (
        <section id="contact" className="py-5">
          <div className="container text-center">
            <h2 className="fw-bold text-white mb-3 js-reveal" data-anim="fadeInUp" data-delay="0.02s">
              Contact
            </h2>

          <div className="glass-pro p-4 mx-auto js-reveal" data-anim="fadeInUp" data-delay="0.06s" style={{ maxWidth: 900 }}>
              <div style={{ color: "rgba(234,240,255,.78)", lineHeight: 1.85 }}>
                <div>DMI Engineering College, Kanyakumari District, Tamil Nadu, India</div>
                <div>Phone: 📞+91 12345 67890 · Email: info@dmicollege.ac.in</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ✅ FOOTER */}
      {!showBuses && (
        <footer style={{ padding: "18px 0 28px" }}>
          <div className="container text-center">
            <div
              style={{
                color: "rgba(234,240,255,.60)",
                fontWeight: 800,
                fontSize: 12.5,
                letterSpacing: 0.2,
                lineHeight: 1.6,
              }}
            >
              <div>Created by: Beryl Jacinth S (JLY)</div>
              <div>CSE – III Year (2023–2027)</div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default HomePage;